/**
 * Gold rush tunables and pure helpers. See docs/bounties.md "Gold rush".
 *
 * Pure and dependency-free, like shared/bounty.ts, so it runs on the server,
 * in the browser and in the Node test runner.
 */
import type { FlashEvent, FlashState } from "./types";
import { addDays, localParts, zonedInstant } from "./weekly.ts";

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

export const FLASH = {
  /** Bonus paid on the one boosted task per peon per rush. */
  multiplier: 5,
  /** How long a rush stays open once started. */
  durationMs: HOUR_MS,
  /** Shortest gap before the next rush is scheduled. */
  minGapMs: 2 * DAY_MS,
  /** Longest gap before the next rush is scheduled. */
  maxGapMs: 5 * DAY_MS,
  /** A rush only starts on the hour, no earlier than this, household time. */
  earliestHour: 9,
  /** A rush only starts on the hour, no later than this, household time. */
  latestHour: 20,
} as const;

/** A gap before the next rush, uniformly distributed between FLASH.minGapMs and FLASH.maxGapMs. */
export function nextFlashDelayMs(rand: () => number): number {
  return FLASH.minGapMs + rand() * (FLASH.maxGapMs - FLASH.minGapMs);
}

/** Whether `event`'s window contains `at` (ms since epoch). Null means no rush. */
export function isFlashActive(event: Pick<FlashEvent, "starts_at" | "ends_at"> | null, at: number): boolean {
  if (!event) return false;
  return Date.parse(event.starts_at) <= at && at < Date.parse(event.ends_at);
}

// ---------- The gold rush pick (docs/bounties.md "Gold rush pick", `rushPickUI`) ----------

/**
 * With `rushPickUI` on for the household the rush is a daily pick: at PICK.nameHour the day's lowest
 * earner is named the picker for the next morning, chooses one open task, and at PICK.startHour the
 * rush boosts that task alone. The whole cycle runs only while the flag is on, since it changes who
 * earns what; with it off rushes are random as before and rushAppliesTo is true for every task.
 */
export const PICK = {
  /** The evening hour, household local time, the picker is named and pushed. */
  nameHour: 20,
  /** The morning hour, household local time, the picked rush starts and the pick closes. */
  startHour: 8,
  /** How late after PICK.startHour a pass may still start the morning's rush (a missed cron, a deploy). */
  startGraceMs: 4 * HOUR_MS,
  /** The most choices the banner lists: chores first, then whatever has waited longest. */
  maxChoices: 5,
} as const;

/** A rush day's clock, all ms since epoch: the day before is the earning day, named at its evening. */
export interface PickWindow {
  /** The rush day, "YYYY-MM-DD" in the household's zone. */
  day: string;
  /** 00:00 of the day before: that day's gold counts. */
  earnFrom: number;
  /** 00:00 six days before that: the week's gold, for ties. */
  weekFrom: number;
  /** PICK.nameHour the evening before: the picker is named from here... */
  nameAt: number;
  /** ...until midnight (a pass after midnight names nobody, so nobody is woken). */
  nameUntil: number;
  /** PICK.startHour on the day: the pick closes and the rush starts. */
  startsAt: number;
  /** The last moment a pass may still start it. */
  startUntil: number;
}

function dayKey(p: { y: number; m: number; d: number }): string {
  return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

function parseDay(day: string): { y: number; m: number; d: number } {
  const [y, m, d] = day.split("-").map(Number);
  return { y, m, d };
}

/** `instant`'s calendar date in `zone`, "YYYY-MM-DD". */
export function localDay(instant: Date, zone: string): string {
  return dayKey(localParts(instant, zone));
}

/** The rush day `day` (household local, "YYYY-MM-DD") in `zone`: when its picker is named, when it starts. */
export function pickWindow(day: string, zone: string): PickWindow {
  const today = parseDay(day);
  const eve = addDays(today.y, today.m, today.d, -1);
  const weekStart = addDays(eve.y, eve.m, eve.d, -6);
  const startsAt = zonedInstant(today.y, today.m, today.d, PICK.startHour, zone).getTime();
  return {
    day,
    earnFrom: zonedInstant(eve.y, eve.m, eve.d, 0, zone).getTime(),
    weekFrom: zonedInstant(weekStart.y, weekStart.m, weekStart.d, 0, zone).getTime(),
    nameAt: zonedInstant(eve.y, eve.m, eve.d, PICK.nameHour, zone).getTime(),
    nameUntil: zonedInstant(today.y, today.m, today.d, 0, zone).getTime(),
    startsAt,
    startUntil: startsAt + PICK.startGraceMs,
  };
}

/**
 * What a housekeeping pass at `now` should do: name tomorrow's picker (between PICK.nameHour and
 * midnight), start today's picked rush (from PICK.startHour, for PICK.startGraceMs), or nothing.
 * Each step is claimed once by the server (server/flash.ts), so overlapping passes are harmless.
 */
export function pickDue(now: Date, zone: string): { phase: "name" | "start"; window: PickWindow } | null {
  const t = now.getTime();
  const todayParts = localParts(now, zone);
  const today = pickWindow(dayKey(todayParts), zone);
  if (t >= today.startsAt && t < today.startUntil) return { phase: "start", window: today };
  const next = addDays(todayParts.y, todayParts.m, todayParts.d, 1);
  const tomorrow = pickWindow(dayKey(next), zone);
  if (t >= tomorrow.nameAt && t < tomorrow.nameUntil) return { phase: "name", window: tomorrow };
  return null;
}

/** One candidate picker: a named peon with a device signed in, and the gold they earned. */
export interface EarnerRow {
  name: string;
  /** Gold earned on the earning day (zero counts). */
  day: number;
  /** Gold earned over the week ending that day, for ties. */
  week: number;
}

/**
 * The picker: the least gold earned that day, ties to the least over the week, then by name so the
 * choice never depends on row order. Null with nobody to choose from.
 */
export function lowestEarner(rows: readonly EarnerRow[]): string | null {
  let best: EarnerRow | null = null;
  for (const r of rows) {
    if (
      !best ||
      r.day < best.day ||
      (r.day === best.day && (r.week < best.week || (r.week === best.week && r.name.toLowerCase() < best.name.toLowerCase())))
    )
      best = r;
  }
  return best?.name ?? null;
}

/** Whether `event` boosts `taskId`: an ordinary rush boosts every task, a picked one only its own. */
export function rushAppliesTo(event: Pick<FlashEvent, "task_id"> | null | undefined, taskId: string | null | undefined): boolean {
  if (!event?.task_id) return true;
  return event.task_id === taskId;
}

/**
 * The gold rush multiplier `taskId` counts with for this peon right now, or null: no rush, the peon
 * already cashed it in, or it is a picked rush for another task. What every card and task screen
 * passes to bountyFor, so the coins agree with the payout (server/flash.ts flashBoostFromRows).
 */
export function boostForTask(flash: FlashState | null | undefined, taskId: string | null | undefined): number | null {
  const active = flash?.active;
  if (!active || flash.claimed || !rushAppliesTo(active, taskId)) return null;
  return active.multiplier;
}

/**
 * What a client shows and counts of the board's FlashState, by its switches: the gold rush (`active`,
 * `claimed`) only while `quickUI` is on, the viewer's late chore rush window (`late_rush`) only while
 * `lateRushUI` is on (docs/bounties.md "Late chore rush"). Null when neither is on or there is none.
 * The board asks for the state while either is on, so this keeps each part behind its own switch.
 */
export function flashForFlags(flash: FlashState | null | undefined, quickUI: boolean, lateRushUI: boolean): FlashState | null {
  if (!flash || (!quickUI && !lateRushUI)) return null;
  return {
    active: quickUI ? flash.active : null,
    claimed: quickUI ? flash.claimed : false,
    late_rush: lateRushUI ? (flash.late_rush ?? null) : null,
  };
}

/** A task the picker may choose: titles only, as the banner lists them. */
export interface PickChoice {
  id: string;
  title: string;
}

/** GET /api/flash/pick: the next rush's pick, as the signed-in peon sees it. Only the picker sees anything. */
export interface FlashPickState {
  /** Whether this peon picks the next gold rush (and the pick is still open). */
  mine: boolean;
  /** The rush day, household local "YYYY-MM-DD", or null. */
  day: string | null;
  /** When the pick closes and the rush starts (ISO), or null. */
  closes_at: string | null;
  /** The task this peon picked, once they have. */
  pick: PickChoice | null;
  /** The open tasks and chores to choose from, while `mine` and nothing is picked yet. */
  choices: PickChoice[];
}

/** Nothing to pick: the state for everyone but the picker, and for everyone with the flag off. */
export const NO_PICK: FlashPickState = { mine: false, day: null, closes_at: null, pick: null, choices: [] };

// Copy, shared by both clients' banners and the server's pushes. Plain and short; pushes carry no numbers.

/** The picker's push, at PICK.nameHour. */
export const PICKER_PUSH = { title: "You pick tomorrow's gold rush", body: "Choose a task before morning." } as const;
/** The banner's head while the picker chooses. */
export const PICK_TITLE = "You pick the next gold rush";
/** The line under it. */
export const PICK_BODY = "Choose a task.";
/** The line under it when nothing is open to choose. */
export const NO_CHOICES = "Nothing open to pick yet.";
/** The confirm before a pick is sent. */
export function pickConfirm(title: string): string {
  return `Make "${title}" the next gold rush?`;
}
/** The banner's head once the picker has chosen. */
export function pickedTitle(title: string): string {
  return `Next gold rush: ${title}`;
}
/** The line under it. */
export const PICKED_BODY = "Your pick. It starts in the morning.";
/** A picked rush's head, on the banner and the push. */
export function pickedRushTitle(title: string): string {
  return `Gold rush: ${title}`;
}
/** Whose pick it was, in small text under the head. */
export function pickerLine(name: string): string {
  return `${name}'s pick`;
}
/** A picked rush's line, before the peon has cashed it in. */
export function pickedRushBody(mins: number, multiplier: number): string {
  return `Finish it in the next ${mins} min for ${multiplier}x gold.`;
}
